import React from "react";
import { Helmet } from "react-helmet-async";
import { getServiceBySlug, getServiceSlugByPath, routeAliases, servicesData } from "../data/services";

const siteUrl = "https://dyzital.com";
const canonicalSiteUrl = `${siteUrl}/`;
const defaultImage = `${siteUrl}/og-dyzital-logo.png`;

const pageMeta = {
  "/": {
    title: "Digital Marketing Services for Growing Businesses | dYzital",
    description:
      "dYzital is a digital marketing company helping businesses grow through SEO, paid ads, social media, websites, automation, and lead generation.",
  },
  "/services": {
    title: "Digital Marketing Company for SEO, Ads, Social Media & Leads | dYzital",
    description:
      "Explore digital marketing services including SEO, paid media, social media marketing, website support, CRM automation, conversion strategy, and lead generation.",
  },
  "/about": {
    title: "About dYzital | Digital Marketing Company",
    description:
      "Learn how dYzital helps businesses improve online visibility, attract the right audience, and generate better enquiries through practical digital marketing.",
  },
  "/contact": {
    title: "Contact dYzital | Digital Marketing Services",
    description:
      "Contact dYzital to discuss your digital marketing needs, business goals, and the best next steps for online growth. Our office is in Madhapur, Hyderabad.",
  },
};

servicesData.forEach((service) => {
  pageMeta[`/services/${service.slug}`] = {
    title: service.metaTitle,
    description: service.metaDescription,
  };
});

Object.entries(routeAliases).forEach(([route, slug]) => {
  const service = getServiceBySlug(slug);
  if (service) {
    pageMeta[route] = {
      title: service.metaTitle,
      description: service.metaDescription,
    };
  }
});

const orgSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
  name: "dYzital",
  legalName: "dYzitalX Software Solutions Pvt. Ltd.",
  url: canonicalSiteUrl,
  logo: `${siteUrl}/og-dyzital-logo.png`,
  image: `${siteUrl}/og-dyzital-logo.png`,
  email: "info@dyzital.com",
  telephone: "+916281506590",
  priceRange: "$$",
  description:
    "dYzital is a digital marketing company in Hyderabad offering SEO, paid media, social media marketing, website support, CRM automation, local SEO, and lead generation services for growing businesses.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "WorkFlo Hitex Bizness Square, Madhapur",
    addressLocality: "Hyderabad",
    addressRegion: "Telangana",
    postalCode: "500081",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 17.45653,
    longitude: 78.37634,
  },
  areaServed: [
    "Madhapur",
    "Hyderabad",
    "Telangana",
    "India",
    "United States",
    "United Kingdom",
    "United Arab Emirates",
    "Australia",
    "Canada",
  ],
  hasMap:
    "https://www.google.com/maps/place/dYzital/@17.45653,78.3737651,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcb93cd779f498b:0x737600281afc24b4!8m2!3d17.45653!4d78.37634!16s%2Fg%2F11z15mw8xh",
  knowsAbout: [
    "Digital marketing services",
    "SEO services",
    "Paid media",
    "Social media marketing",
    "Website marketing",
    "Lead generation",
    "CRM automation",
    "Local SEO",
    "Digital marketing for clinics",
    "Healthcare marketing",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+916281506590",
    email: "info@dyzital.com",
    contactType: "customer support",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi", "Telugu"],
  },
  sameAs: [
    "https://www.instagram.com/dyzital",
    "https://www.linkedin.com/company/dyzital",
  ],
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "dYzital Digital Marketing Services",
  itemListElement: servicesData.map((service, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Service",
      name: service.routeTitle,
      url: `${siteUrl}/services/${service.slug}`,
      provider: {
        "@type": "Organization",
        name: "dYzital",
        url: canonicalSiteUrl,
      },
      areaServed: "India",
    },
  })),
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What digital marketing services does dYzital offer?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "dYzital offers SEO, paid ads, social media marketing, website support, CRM automation, analytics, conversion optimization, and lead generation services.",
      },
    },
    {
      "@type": "Question",
      name: "Where is dYzital located?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "dYzital is based in Hyderabad, Telangana, India and supports businesses across service areas.",
      },
    },
    {
      "@type": "Question",
      name: "Who does dYzital help?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "dYzital helps clinics, hospitals, restaurants, local service businesses, real estate teams, and growing companies improve visibility, lead quality, and conversion performance.",
      },
    },
  ],
};

function Seo({ pathname }) {
  const activeServiceSlug = getServiceSlugByPath(pathname);
  const activeService = activeServiceSlug ? getServiceBySlug(activeServiceSlug) : null;
  const cleanPath = pageMeta[pathname] ? pathname : "/";
  const meta = pageMeta[cleanPath];
  const canonical = `${siteUrl}${cleanPath === "/" ? "/" : cleanPath}`;
  const activeFaqSchema = activeService
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: activeService.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      }
    : cleanPath === "/"
      ? faqSchema
      : null;
  const activeServiceSchema = activeService
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: activeService.routeTitle,
        description: activeService.metaDescription,
        url: canonical,
        provider: {
          "@type": "Organization",
          name: "dYzital",
          url: canonicalSiteUrl,
          telephone: "+916281506590",
          email: "info@dyzital.com",
        },
        areaServed: ["Hyderabad", "Telangana", "India"],
        serviceType: activeService.shortTitle,
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: `${activeService.shortTitle} deliverables`,
          itemListElement: activeService.bullets.map((item) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: item,
            },
          })),
        },
      }
    : null;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta
        name="keywords"
        content="digital marketing services, digital marketing company, digital marketing agency, digital marketing in hyderabad, digital marketing services in madhapur hyderabad, SEO services, paid ads, social media marketing, website marketing, lead generation, CRM automation"
      />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="dYzital" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={defaultImage} />
      <meta property="og:image:secure_url" content={defaultImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="693" />
      <meta property="og:image:height" content="665" />
      <meta property="og:image:alt" content="dYzital logo" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={defaultImage} />
      <meta name="twitter:image:alt" content="dYzital logo" />

      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      {activeServiceSchema && (
        <script type="application/ld+json">{JSON.stringify(activeServiceSchema)}</script>
      )}
      {activeFaqSchema && (
        <script type="application/ld+json">{JSON.stringify(activeFaqSchema)}</script>
      )}
    </Helmet>
  );
}

export default Seo;
